import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

export type AiCommandContext = {
  sourceModule?: string;
  sourceRefId?: string | null;
  locale?: string | null;
  timezone?: string | null;
};

export type AiCommandRequest = {
  prompt: string;
  context?: Record<string, unknown>;
  source?: AiCommandContext;
};

export type AiChatRequest = AiCommandRequest;

export type AiChatResponse = {
  responseMode?: 'MODEL_DRAFT' | 'EVIDENCE_GUIDANCE' | 'MOCK';
  contextVersion?: string | null;
  id?: null;
  organizationId?: null;
  status?: null;
  intent?: null;
  summary?: string | null;
  confidence?: number | null;
  requiresApproval?: null;
  missingFields?: null;
  actions?: null;
  draft?: null;
  answer?: string | null;
  suggestions?: string[] | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiMaintenanceTriageRequest = {
  title: string;
  description: string;
  propertyName?: string | null;
  unitLabel?: string | null;
  tenantName?: string | null;
  reportedBy?: string | null;
  currentStatus?: string | null;
  context?: AiCommandContext | null;
};

export type AiMaintenanceTriageResponse = {
  category?: string | null;
  priority?: string | null;
  recommendedStatus?: string | null;
  urgencyReason?: string | null;
  suggestedAssigneeType?: string | null;
  suggestedSlaTarget?: string | null;
  safetyFlags?: string[] | null;
  confidence?: number | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiMessageDraftRequest = {
  purpose: string;
  recipientType: string;
  tone?: string | null;
  contextText?: string | null;
  context?: AiCommandContext | null;
};

export type AiMessageDraftResponse = {
  responseMode?: 'MODEL_DRAFT' | 'EVIDENCE_GUIDANCE' | 'MOCK';
  contextVersion?: string | null;
  subject?: string | null;
  body?: string | null;
  cautions?: string[] | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiProposedAction = {
  service?: string | null;
  operation?: string | null;
  method?: string | null;
  endpoint?: string | null;
  payload?: Record<string, unknown> | null;
  requiresApproval?: boolean | null;
};

export type AiDraftQuestion = {
  field?: string | null;
  label?: string | null;
  question?: string | null;
  required?: boolean | null;
  type?: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'reference' | string | null;
  options?: string[] | null;
};

export type AiDraftStatus =
  | 'PENDING_REVIEW'
  | 'NEEDS_MORE_INFO'
  | 'APPROVED'
  | 'REJECTED'
  | 'EXECUTION_PENDING'
  | 'EXECUTED'
  | 'FAILED'
  | 'EXPIRED'
  | string;

export type AiDraftResponse = {
  executionProtocol?: string | null;
  execution?: AiExecutionStatus | null;
  id: string;
  organizationId: string;
  status: AiDraftStatus;
  intent?: string | null;
  prompt?: string | null;
  summary?: string | null;
  confidence?: number | null;
  requiresApproval?: boolean | null;
  missingFields?: string[] | null;
  draft?: Record<string, unknown> | null;
  actions?: AiProposedAction[] | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  executedAt?: string | null;
};

export type UpdateAiDraftRequest = {
  expectedUpdatedAt?: string | null;
  summary?: string | null;
  missingFields?: string[] | null;
  draft?: Record<string, unknown> | null;
  actions?: AiProposedAction[] | null;
};

export type AiDraftDecisionRequest = {
  expectedUpdatedAt?: string | null;
  executionHash?: string | null;
  notes?: string | null;
  requestExecution?: boolean | null;
};

export type AiExecutionPreview = {
  draftId: string;
  expectedUpdatedAt: string;
  executionHash: string;
  payloadHash: string;
  operation: string;
  payload: Record<string, unknown>;
  warnings: string[];
};

export type AiExecutionStatus = {
  state: 'IN_FLIGHT' | 'UNKNOWN' | 'REJECTED' | 'SUCCEEDED';
  executionHash: string;
  payload: Record<string, unknown>;
  retryAfter?: string | null;
  result?: { type?: string; id?: string } | null;
};

export type AiActionHistoryResponse = {
  id: string;
  draftId: string;
  actionType: string;
  actorUserId?: string | null;
  actorEmail?: string | null;
  notes?: string | null;
  payload?: Record<string, unknown> | null;
  createdAt?: string | null;
};

export type AiInspectionPhotoRef = {
  mediaId?: string | null;
  fileUrl: string;
  stage?: string | null;
  areaTag?: string | null;
  caption?: string | null;
  contentType?: string | null;
};

export type AiInspectionItemSnapshot = {
  itemId?: string | null;
  area?: string | null;
  category?: string | null;
  label?: string | null;
  condition?: string | null;
  passed?: boolean | null;
  notes?: string | null;
  needsMaintenance?: boolean | null;
};

export type AiInspectionAnalysisRequest = {
  inspectionId: string;
  inspectionType?: string | null;
  propertyName?: string | null;
  unitLabel?: string | null;
  tenantName?: string | null;
  summary?: string | null;
  notes?: string | null;
  photos?: AiInspectionPhotoRef[] | null;
  items?: AiInspectionItemSnapshot[] | null;
  context?: AiCommandContext | null;
};

export type AiDetectedInspectionIssue = {
  area?: string | null;
  category?: string | null;
  title?: string | null;
  description?: string | null;
  severity?: string | null;
  maintenanceRecommended?: boolean | null;
  mediaIds?: string[] | null;
  confidence?: number | null;
};

export type AiSuggestedMaintenanceTicket = {
  title?: string | null;
  description?: string | null;
  category?: string | null;
  priority?: string | null;
  area?: string | null;
  mediaIds?: string[] | null;
};

export type AiInspectionAnalysisResponse = {
  summary?: string | null;
  roomTags?: string[] | null;
  issues?: AiDetectedInspectionIssue[] | null;
  suggestedMaintenance?: AiSuggestedMaintenanceTicket[] | null;
  report?: string | null;
  confidence?: number | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiInspectionComparisonRequest = {
  inspectionId: string;
  compareToInspectionId?: string | null;
  inspectionType?: string | null;
  propertyName?: string | null;
  unitLabel?: string | null;
  currentPhotos?: AiInspectionPhotoRef[] | null;
  baselinePhotos?: AiInspectionPhotoRef[] | null;
  context?: AiCommandContext | null;
};

export type AiInspectionComparisonResponse = {
  summary?: string | null;
  changes?: Array<Record<string, unknown>> | null;
  suggestedMaintenance?: AiSuggestedMaintenanceTicket[] | null;
  confidence?: number | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiInspectionReportRequest = {
  inspectionId: string;
  inspectionType?: string | null;
  propertyName?: string | null;
  unitLabel?: string | null;
  tenantName?: string | null;
  analysis?: Record<string, unknown> | null;
  photos?: AiInspectionPhotoRef[] | null;
  items?: AiInspectionItemSnapshot[] | null;
  context?: AiCommandContext | null;
};

export type AiInspectionReportResponse = {
  title?: string | null;
  executiveSummary?: string | null;
  conditionSummary?: string[] | null;
  tenantNotes?: string[] | null;
  maintenanceRecommendations?: string[] | null;
  reportMarkdown?: string | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiDocumentClassificationRequest = {
  documentId?: string | null;
  fileName?: string | null;
  contentType?: string | null;
  text?: string | null;
  fileUrl?: string | null;
  context?: AiCommandContext | null;
};

export type AiDocumentClassificationResponse = {
  documentType?: string | null;
  category?: string | null;
  extractedFields?: Record<string, unknown> | null;
  warnings?: string[] | null;
  confidence?: number | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiDataQueryRequest = {
  question: string;
  dataContext?: Record<string, unknown> | null;
  context?: AiCommandContext | null;
};

export type AiDataQueryResponse = {
  answer?: string | null;
  rows?: Array<Record<string, unknown>> | null;
  suggestedFollowUps?: string[] | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiTransactionAnomalyRequest = {
  period?: string | null;
  currency?: string | null;
  transactions?: Array<Record<string, unknown>> | null;
  context?: AiCommandContext | null;
};

export type AiTransactionAnomalyResponse = {
  summary?: string | null;
  anomalies?: Array<Record<string, unknown>> | null;
  recommendedActions?: string[] | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

export type AiRentSuggestionRequest = {
  unitId: string;
  propertyName?: string | null;
  unitLabel?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  squareFeet?: number | null;
  currentRent?: number | null;
  currency?: string | null;
  features?: Record<string, unknown> | null;
  comparables?: Array<Record<string, unknown>> | null;
  rentHistory?: number[] | null;
  context?: AiCommandContext | null;
};

export type AiRentSuggestionResponse = {
  recommendedRent?: number | null;
  lowRange?: number | null;
  highRange?: number | null;
  currency?: string | null;
  rationale?: string[] | null;
  caveats?: string[] | null;
  confidence?: number | null;
  provider?: string | null;
  model?: string | null;
  auditId?: string | null;
};

@Injectable({ providedIn: 'root' })
export class AiApiService {
  private readonly http = inject(HttpClient);
  private readonly aiBase = `${environment.apiGatewayUrl}/ai-service`;

  assistantTurn(orgId: string, payload: AiCommandRequest & { activeDraftId?: string | null; expectedUpdatedAt?: string | null }): Observable<{ message: AiChatResponse | null; draft: AiDraftResponse | null }> {
    return this.http.post<{ message: AiChatResponse | null; draft: AiDraftResponse | null }>(
      `${this.aiBase}/organizations/${orgId}/assistant/turn`, payload, { withCredentials: true });
  }

  interpretCommand(orgId: string, payload: AiCommandRequest): Observable<AiDraftResponse> {
    return this.http.post<AiDraftResponse>(
      `${this.aiBase}/organizations/${orgId}/commands/interpret`,
      payload,
      { withCredentials: true },
    );
  }

  chat(orgId: string, payload: AiChatRequest): Observable<AiChatResponse> {
    return this.http.post<AiChatResponse>(
      `${this.aiBase}/organizations/${orgId}/chat`,
      payload,
      { withCredentials: true },
    );
  }

  triageMaintenance(orgId: string, payload: AiMaintenanceTriageRequest): Observable<AiMaintenanceTriageResponse> {
    return this.http.post<AiMaintenanceTriageResponse>(
      `${this.aiBase}/organizations/${orgId}/maintenance/triage`,
      payload,
      { withCredentials: true },
    );
  }

  draftMessage(orgId: string, payload: AiMessageDraftRequest): Observable<AiMessageDraftResponse> {
    return this.http.post<AiMessageDraftResponse>(
      `${this.aiBase}/organizations/${orgId}/messages/draft`,
      payload,
      { withCredentials: true },
    );
  }

  validateReplyContext(orgId: string, payload: { context: AiCommandContext; recipientType: string; expectedVersion: string }): Observable<{ valid: boolean; contextVersion: string }> {
    return this.http.post<{ valid: boolean; contextVersion: string }>(
      `${this.aiBase}/organizations/${orgId}/context/validate`, payload, { withCredentials: true },
    );
  }

  listDrafts(orgId: string, status?: AiDraftStatus | null): Observable<AiDraftResponse[]> {
    const url = status
      ? `${this.aiBase}/organizations/${orgId}/drafts?status=${encodeURIComponent(status)}`
      : `${this.aiBase}/organizations/${orgId}/drafts`;
    return this.http.get<AiDraftResponse[]>(url, { withCredentials: true });
  }

  listDraftPage(orgId: string, cursor?: string | null): Observable<{ content: AiDraftResponse[]; nextCursor: string | null }> {
    const query = new URLSearchParams({ limit: '10' });
    if (cursor) query.set('cursor', cursor);
    return this.http.get<{ content: AiDraftResponse[]; nextCursor: string | null }>(
      `${this.aiBase}/organizations/${orgId}/drafts/page?${query}`, { withCredentials: true },
    );
  }

  getDraft(orgId: string, draftId: string): Observable<AiDraftResponse> {
    return this.http.get<AiDraftResponse>(
      `${this.aiBase}/organizations/${orgId}/drafts/${draftId}`,
      { withCredentials: true },
    );
  }

  updateDraft(orgId: string, draftId: string, payload: UpdateAiDraftRequest): Observable<AiDraftResponse> {
    return this.http.patch<AiDraftResponse>(
      `${this.aiBase}/organizations/${orgId}/drafts/${draftId}`,
      payload,
      { withCredentials: true },
    );
  }

  previewExecution(orgId: string, draftId: string, expectedUpdatedAt: string): Observable<AiExecutionPreview> {
    return this.http.post<AiExecutionPreview>(`${this.aiBase}/organizations/${orgId}/drafts/${draftId}/execution-preview`,
      { expectedUpdatedAt }, { withCredentials: true });
  }

  getExecution(orgId: string, draftId: string): Observable<{ execution: AiExecutionStatus | null }> {
    return this.http.get<{ execution: AiExecutionStatus | null }>(
      `${this.aiBase}/organizations/${orgId}/drafts/${draftId}/execution`, { withCredentials: true });
  }

  retryExecution(orgId: string, draftId: string, payload: AiDraftDecisionRequest): Observable<AiDraftResponse> {
    return this.http.post<AiDraftResponse>(`${this.aiBase}/organizations/${orgId}/drafts/${draftId}/execution/retry`,
      payload, { withCredentials: true });
  }

  approveDraft(orgId: string, draftId: string, payload: AiDraftDecisionRequest): Observable<AiDraftResponse> {
    return this.http.post<AiDraftResponse>(
      `${this.aiBase}/organizations/${orgId}/drafts/${draftId}/approve`,
      payload,
      { withCredentials: true },
    );
  }

  rejectDraft(orgId: string, draftId: string, payload: AiDraftDecisionRequest): Observable<AiDraftResponse> {
    return this.http.post<AiDraftResponse>(
      `${this.aiBase}/organizations/${orgId}/drafts/${draftId}/reject`,
      payload,
      { withCredentials: true },
    );
  }

  getDraftHistory(orgId: string, draftId: string): Observable<AiActionHistoryResponse[]> {
    return this.http.get<AiActionHistoryResponse[]>(
      `${this.aiBase}/organizations/${orgId}/drafts/${draftId}/history`,
      { withCredentials: true },
    );
  }

  analyzeInspection(orgId: string, inspectionId: string, payload: AiInspectionAnalysisRequest): Observable<AiInspectionAnalysisResponse> {
    return this.http.post<AiInspectionAnalysisResponse>(
      `${this.aiBase}/organizations/${orgId}/inspections/${inspectionId}/analyze`,
      { ...payload, inspectionId },
      { withCredentials: true },
    );
  }

  compareInspection(orgId: string, inspectionId: string, payload: AiInspectionComparisonRequest): Observable<AiInspectionComparisonResponse> {
    return this.http.post<AiInspectionComparisonResponse>(
      `${this.aiBase}/organizations/${orgId}/inspections/${inspectionId}/compare`,
      { ...payload, inspectionId },
      { withCredentials: true },
    );
  }

  generateInspectionReport(orgId: string, inspectionId: string, payload: AiInspectionReportRequest): Observable<AiInspectionReportResponse> {
    return this.http.post<AiInspectionReportResponse>(
      `${this.aiBase}/organizations/${orgId}/inspections/${inspectionId}/report`,
      { ...payload, inspectionId },
      { withCredentials: true },
    );
  }

  classifyDocument(orgId: string, payload: AiDocumentClassificationRequest): Observable<AiDocumentClassificationResponse> {
    return this.http.post<AiDocumentClassificationResponse>(
      `${this.aiBase}/organizations/${orgId}/documents/classify`,
      payload,
      { withCredentials: true },
    );
  }

  queryData(orgId: string, payload: AiDataQueryRequest): Observable<AiDataQueryResponse> {
    return this.http.post<AiDataQueryResponse>(
      `${this.aiBase}/organizations/${orgId}/assistant/query`,
      payload,
      { withCredentials: true },
    );
  }

  reviewTransactionAnomalies(orgId: string, payload: AiTransactionAnomalyRequest): Observable<AiTransactionAnomalyResponse> {
    return this.http.post<AiTransactionAnomalyResponse>(
      `${this.aiBase}/organizations/${orgId}/transactions/anomalies/review`,
      payload,
      { withCredentials: true },
    );
  }

  suggestRent(orgId: string, unitId: string, payload: AiRentSuggestionRequest): Observable<AiRentSuggestionResponse> {
    return this.http.post<AiRentSuggestionResponse>(
      `${this.aiBase}/organizations/${orgId}/units/${unitId}/rent-suggestion`,
      { ...payload, unitId },
      { withCredentials: true },
    );
  }
}
